// hooks/taxi/useTaxiManifest.js

import {
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";

import {
    assignTaxiTeamToManifest,
    unassignTaxiTeamFromManifest,
    createIntegratedTaxiTeam,
    fetchTaxiTeams,
    finishTaxiManifest,
} from "../../api/taxi/taxiManifestApi";

/**
 * Retorna un entero positivo o null.
 *
 * Se utiliza para construir query keys estables
 * y evitar valores como NaN o undefined.
 */
const toPositiveIntegerOrNull = (value) => {
    const parsedValue = Number(value);

    if (
        !Number.isInteger(parsedValue) ||
        parsedValue <= 0
    ) {
        return null;
    }

    return parsedValue;
};

/**
 * Query keys centralizadas.
 *
 * Esto evita inconsistencias al invalidar consultas.
 */
export const taxiManifestKeys = {
    teamsRoot: ["taxi-teams"],

    teams: ({
        manifestId,
        serProId,
    }) => [
            "taxi-teams",
            "manifest",
            toPositiveIntegerOrNull(manifestId),
            "service-project",
            toPositiveIntegerOrNull(serProId),
        ],
};

/**
 * Obtiene los equipos compatibles con un manifiesto
 * y su servicio-proyecto.
 */
export const useTaxiTeams = ({
    manifestId,
    serProId,
    enabled = true,
} = {}) => {
    const safeManifestId =
        toPositiveIntegerOrNull(manifestId);

    const safeSerProId =
        toPositiveIntegerOrNull(serProId);

    return useQuery({
        queryKey: taxiManifestKeys.teams({
            manifestId: safeManifestId,
            serProId: safeSerProId,
        }),

        queryFn: () =>
            fetchTaxiTeams({
                manifestId: safeManifestId,
                serProId: safeSerProId,
                active: true,
            }),

        enabled: Boolean(
            enabled &&
            safeManifestId &&
            safeSerProId
        ),

        staleTime: 30 * 1000,

        gcTime: 5 * 60 * 1000,

        refetchOnWindowFocus: false,

        retry: false,

        select: (response) => {
            if (Array.isArray(response)) {
                return response;
            }

            if (Array.isArray(response?.data)) {
                return response.data;
            }

            return [];
        },
    });
};

/**
 * Crea de forma integrada:
 *
 * - Persona
 * - Empleado/usuario
 * - Vehículo
 * - Relación empleado-servicio
 * - Relación vehículo-servicio
 * - Equipo conductor-vehículo
 */
export const useCreateIntegratedTaxiTeam = () => {
    const queryClient =
        useQueryClient();

    return useMutation({
        mutationFn:
            createIntegratedTaxiTeam,

        onSuccess: async (
            _response,
            variables
        ) => {
            const serProId =
                toPositiveIntegerOrNull(
                    variables?.equipo?.serProId
                );

            await Promise.all([
                /*
                 * Refresca todas las consultas de equipos.
                 */
                queryClient.invalidateQueries({
                    queryKey:
                        taxiManifestKeys.teamsRoot,
                }),

                /*
                 * Refresca empleados porque se creó
                 * un nuevo empleado.
                 */
                queryClient.invalidateQueries({
                    queryKey: ["employees"],
                }),

                /*
                 * Refresca vehículos porque se creó
                 * un nuevo vehículo.
                 */
                queryClient.invalidateQueries({
                    queryKey: ["vehicles"],
                }),

                /*
                 * Invalida consultas relacionadas con
                 * el servicio-proyecto, si existen.
                 */
                queryClient.invalidateQueries({
                    queryKey: [
                        "services-project",
                        serProId,
                    ],
                }),

                queryClient.invalidateQueries({
                    queryKey: [
                        "service-project",
                        serProId,
                    ],
                }),
            ]);
        },

        retry: false,
    });
};

/**
 * Asigna un equipo registrado a un manifiesto.
 */
export const useAssignTaxiTeam = () => {
    const queryClient =
        useQueryClient();

    return useMutation({
        mutationFn:
            assignTaxiTeamToManifest,

        onSuccess: async (
            _response,
            variables
        ) => {
            const manifestId =
                toPositiveIntegerOrNull(
                    variables?.manifestId
                );

            await Promise.all([
                // Lista general y paginada de manifiestos.                
                queryClient.invalidateQueries({ queryKey: ["manifests"], }),
                queryClient.invalidateQueries({ queryKey: ["manifests", "paged",], }),

                //para el detalle del manifiesto.
                queryClient.invalidateQueries({ queryKey: ["manifest", manifestId,], }),
                queryClient.invalidateQueries({ queryKey: ["manifests", manifestId,], }),

                // Pasajeros del manifiesto.
                queryClient.invalidateQueries({ queryKey: ["pasajeros", manifestId,], }),


                // Equipos, porque la disponibilidad
                // puede haber cambiado.                
                queryClient.invalidateQueries({ queryKey: taxiManifestKeys.teamsRoot, }),
            ]);
        },

        retry: false,
    });
};

/**
 * Desasigna el equipo actual de un manifiesto.
 */
export const useUnassignTaxiTeam = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: unassignTaxiTeamFromManifest,

        onSuccess: async (data, variables) => {
            const manifestId = toPositiveIntegerOrNull(variables?.manifestId);

            await Promise.all([
                // Invalidar listas de manifiestos
                queryClient.invalidateQueries({ queryKey: ["manifests"] }),
                queryClient.invalidateQueries({ queryKey: ["manifests", "paged"] }),

                // Detalle del manifiesto específico
                queryClient.invalidateQueries({ queryKey: ["manifest", manifestId] }),
                queryClient.invalidateQueries({ queryKey: ["manifests", manifestId] }),

                // Pasajeros (por si cambia algo)
                queryClient.invalidateQueries({ queryKey: ["pasajeros", manifestId] }),

                // Equipos disponibles (cambia disponibilidad)
                queryClient.invalidateQueries({ queryKey: taxiManifestKeys.teamsRoot }),
            ]);

            // Opcional: si la respuesta contiene el nuevo estado, podrías actualizar el cache manualmente
            // queryClient.setQueryData(["manifest", manifestId], (old) => ({ ...old, manest: data.newState }));
        },

        retry: false,
    });
};

/**
 * Finaliza un manifiesto de taxi.
 * Solo permite finalizar si está en EN_PROCESO.
 */
export const useFinishTaxiManifest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: finishTaxiManifest,

        onSuccess: async (data, variables) => {
            const manifestId = toPositiveIntegerOrNull(variables?.manifestId);

            // Invalidar consultas relacionadas con el manifiesto
            await Promise.all([
                // Listas generales
                queryClient.invalidateQueries({ queryKey: ["manifests"] }),
                queryClient.invalidateQueries({ queryKey: ["manifests", "paged"] }),

                // Detalle del manifiesto específico
                queryClient.invalidateQueries({ queryKey: ["manifest", manifestId] }),
                queryClient.invalidateQueries({ queryKey: ["manifests", manifestId] }),

                // Pasajeros (porque cambian de estado)
                queryClient.invalidateQueries({ queryKey: ["pasajeros", manifestId] }),

                // Equipos (puede cambiar disponibilidad)
                queryClient.invalidateQueries({ queryKey: taxiManifestKeys.teamsRoot }),
            ]);

            // Opcional: actualizar el cache manualmente con el nuevo estado
            queryClient.setQueryData(["manifest", manifestId], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    manest: "COMPLETADO",
                    // También puedes actualizar otros campos si la respuesta lo incluye
                };
            });
        },

        retry: false,
    });
};